import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ProjectOverview() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    fetch(`${API}/companies/${companyId}/projects/overview`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [companyId, API]);

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;
  if (initialLoading) return <div style={pageStyle}><p>Loading...</p></div>;
  if (!data) return <div style={pageStyle}><p>Failed to load project overview.</p></div>;

  const s = data.summary || {};

  const summaryCards = [
    { label: 'Total Projects', value: s.total_projects, color: '#3b82f6' },
    { label: 'Active', value: s.active_projects, color: '#10b981' },
    { label: 'On Track', value: s.on_track, color: '#22c55e' },
    { label: 'At Risk', value: s.at_risk, color: '#f59e0b' },
    { label: 'Delayed', value: s.delayed, color: '#ef4444' },
    { label: 'On Hold', value: s.on_hold, color: '#6b7280' },
    { label: 'Completed', value: s.completed, color: '#8b5cf6' },
  ];

  const taskCards = [
    { label: 'Open Tasks', value: s.open_tasks, color: '#3b82f6' },
    { label: 'Overdue Tasks', value: s.overdue_tasks, color: '#ef4444' },
    { label: 'Upcoming Milestones', value: s.upcoming_milestones, color: '#f59e0b' },
    { label: 'Overdue Milestones', value: s.overdue_milestones, color: '#dc2626' },
    { label: 'Pending Deliverables', value: s.pending_deliverables, color: '#8b5cf6' },
    { label: 'Running Timers', value: s.running_timers, color: '#06b6d4' },
  ];

  const healthColor = { on_track: '#22c55e', at_risk: '#f59e0b', delayed: '#ef4444', critical: '#dc2626' };
  const priorityColor = { low: '#6b7280', normal: '#3b82f6', high: '#f59e0b', critical: '#ef4444' };

  return (
    <div style={pageStyle}>
      <h2 style={{ margin: '0 0 20px', fontSize: '1.5rem', fontWeight: 700 }}>📊 Project Overview</h2>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {summaryCards.map(c => (
          <div key={c.label} style={cardStyle}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>{c.label}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: c.color }}>{c.value || 0}</div>
          </div>
        ))}
      </div>

      {/* Task & Milestone Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        {taskCards.map(c => (
          <div key={c.label} style={cardStyle}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>{c.label}</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: c.color }}>{c.value || 0}</div>
          </div>
        ))}
      </div>

      {/* Pending Actions */}
      {Array.isArray(data.pending_actions) && data.pending_actions.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: '20px', padding: '16px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 }}>⚠️ Pending Actions</h3>
          {data.pending_actions.map((a, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '0.85rem' }}>{a.label}</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#ef4444' }}>{a.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* Active Projects Table */}
      <div style={{ ...cardStyle, padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Active Projects</h3>
          <button onClick={() => navigate('/company/projects/directory')} style={linkBtnStyle}>View All →</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Project</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Manager</th>
                <th style={thStyle}>Health</th>
                <th style={thStyle}>Progress</th>
                <th style={thStyle}>Priority</th>
                <th style={thStyle}>Open Tasks</th>
                <th style={thStyle}>Overdue</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(data.active_projects) && data.active_projects.map(p => (
                <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/company/projects/${p.id}`)}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600 }}>{p.name}</div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{p.project_code}</div>
                  </td>
                  <td style={tdStyle}>{p.project_type?.name || '-'}</td>
                  <td style={tdStyle}>{p.project_manager?.name || '-'}</td>
                  <td style={tdStyle}>
                    <span style={{ ...badgeStyle, backgroundColor: healthColor[p.health] || '#6b7280' }}>
                      {(p.health || '').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '60px', height: '6px', backgroundColor: '#e2e8f0', borderRadius: '3px' }}>
                        <div style={{ width: `${p.progress_percentage || 0}%`, height: '100%', backgroundColor: '#3b82f6', borderRadius: '3px' }} />
                      </div>
                      <span style={{ fontSize: '0.75rem' }}>{p.progress_percentage || 0}%</span>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ ...badgeStyle, backgroundColor: priorityColor[p.priority] || '#6b7280' }}>
                      {p.priority}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{p.open_tasks_count || 0}</td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: (p.overdue_tasks_count || 0) > 0 ? '#ef4444' : 'inherit' }}>
                    {p.overdue_tasks_count || 0}
                  </td>
                </tr>
              ))}
              {(!data.active_projects || data.active_projects.length === 0) && (
                <tr><td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No active projects</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1400px', margin: '0 auto' };
const cardStyle = { backgroundColor: '#fff', borderRadius: '8px', padding: '12px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e2e8f0', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' };
const tdStyle = { padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };
const badgeStyle = { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', color: '#fff', fontWeight: 600, textTransform: 'capitalize' };
const linkBtnStyle = { background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 };
